import axios from 'axios';
import { AuthState } from '@okta/okta-auth-js';

export interface CompletenessAnalysis {
  status: 'compliant' | 'partially-compliant' | 'non-compliant';
  findings: Array<{
    code: string;       // Spectral rule name
    message: string;     // description of the violation
    severity: 'error' | 'warn' | 'info';
    line?: number;        // 1-indexed line number in the spec
  }>;
  specFormat?: 'openapi' | 'asyncapi' | 'wsdl';
  lastAnalyzedAt?: string; // ISO timestamp
  specLastUpdatedAt?: string; // ISO timestamp
}

// For backwards compatibility
export type ReportsAnalysis = CompletenessAnalysis;

export interface ApiVersion {
  id: string;
  versionId: string;
  isLatestVersion?: boolean;
  completeness?: CompletenessAnalysis | null;
}

export interface ApiAnalysisResponse {
  id: string;
  fullApiName: string;
  isParentAggregate?: boolean; // true when this record has no spec of its own; pick a version
  apiVersions?: ApiVersion[];
  apiCenterMetadata?: {
    source: string;
    completeness?: CompletenessAnalysis | null;
    healthScore?: {
      score: number;
      breakdown?: {
        compliance: number;
        quality: number;
      };
      lastCalculated?: string;
    } | null;
  };
  specText?: string; // For OpenAPI/AsyncAPI specs (not WSDL)
}

export interface DashboardMetrics {
  totalApisMonitored: number;
  compliantAPIs: number;
  partiallyCompliantAPIs: number;
  notAnalyzedAPIs: number;
  overallComplianceRate: number;
  lastSnapshotDate: string;
  criticalAlerts: number;
}

export interface ReportRow {
  apiId: string;
  fullApiName: string;
  isEnrolled: boolean;
  lineOfBusiness?: string;
  product?: string;
  area?: string;
  apiVersion?: string;
  pointOfContact?: string;
  status: 'compliant' | 'partially-compliant' | 'non-compliant' | 'not-analyzed';
  qualityScore?: number;
  complianceScore?: number;
  findings?: Array<{
    code: string;
    message: string;
    severity: 'error' | 'warn' | 'info';
    line?: number;
  }>;
  lastAnalyzedAt?: string;
  lifecycleStage?: string;

  // APIM Coverage additions
  developerProgram?: boolean;
  apimPublishedStatus?: 'yes' | 'no' | 'n/a';
  apimEnvironments?: string;
  apimProducts?: string;
  apimLastDiscoveredAt?: string;

  // Child versions (used by both APIM and Governance views)
  childVersions?: Array<{
    versionId: string;
    versionNumber: string;

    // APIM-specific fields
    apimPublishedStatus?: 'yes' | 'no' | 'n/a';
    apimEnvironments?: string;
    apimLastDiscoveredAt?: string;

    // Governance-specific fields
    status?: 'compliant' | 'partially-compliant' | 'non-compliant' | 'not-analyzed';
    qualityScore?: number;
    complianceScore?: number;
    findings?: Array<{
      code: string;
      message: string;
      severity: 'error' | 'warn' | 'info';
      line?: number;
    }>;
    lastAnalyzedAt?: string;
    isEnrolled?: boolean;
    lifecycleStage?: string;
  }>;
}

export interface ReportResponse {
  data: ReportRow[];
  total: number;
  page: number;
  pageSize: number;
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  summary?: {
    totalApisAnalyzed: number;
    pipelineEnrolledCount: number;
    compliantCount: number;
    partiallyCompliantCount: number;
    totalFindings: number;
  };
}

export interface RunAnalysisResponse {
  message: string;
  timestamp: string;
  results: {
    totalApis: number;
    analyzed: number;
    skipped: number;
    failed: number;
    details: Array<{
      apiId: string;
      status: string;
      error?: string;
    }>;
  };
}

export interface SingleApiAnalysisResponse {
  message: string;
  timestamp: string;
  analysis: ReportsAnalysis;
}

export interface ReportSnapshot {
  id: string;
  reportDate: string; // YYYY-MM-DD
  generatedAt: string; // ISO timestamp
  period: 'weekly';
  
  totalApisMonitored: number;
  totalApisAnalyzed: number;
  notAnalyzedCount: number;
  
  complianceStats: {
    compliant: number;
    partiallyCompliant: number;
    nonCompliant: number;
    notAnalyzed: number;
  };
  overallComplianceRate: number;
  
  totalFindingsCount: number;
  averageFindingsPerApi: number;
  findingsBySeverity: {
    errors: number;
    warnings: number;
    info: number;
  };
  
  reportData: ReportRow[];
}

export type ReportSnapshotSummary = Omit<ReportSnapshot, 'reportData'>;

export interface SnapshotsResponse {
  snapshots: ReportSnapshotSummary[];
  total: number;
  hasMore: boolean;
}

/**
 * Service for calling reports analysis backend endpoints
 */
class ReportsService {
   public readonly apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

  private getHeaders(authState: AuthState | null): { headers: Record<string, string> } {
    if (!authState) {
      throw new Error('Auth state is not available.');
    }
    const token = authState?.accessToken?.accessToken;
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    };
  }

  /**
   * GET /api/admin/reports
   * Fetch reports for all APIs with optional filters
   */
  async getReport(
    authState: AuthState | null,
    search?: string,
    ownerTeam?: string,
    lineOfBusiness?: string,
    product?: string,
    area?: string,
    pointOfContact?: string,
    status?: 'compliant' | 'partially-compliant' | 'non-compliant' | 'not-analyzed' | '',
    enrolled?: boolean,
    page: number = 1,
    pageSize: number = 20,
    format: 'json' | 'csv' = 'json',
    developerProgram?: boolean,
    apimPublishedStatus?: string,
    lifecycleStage?: string
  ): Promise<ReportResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        format,
      });

      if (search) params.append('search', search);
      if (ownerTeam) params.append('ownerTeam', ownerTeam);
      if (lineOfBusiness) params.append('lineOfBusiness', lineOfBusiness);
      if (product) params.append('product', product);
      if (area) params.append('area', area);
      if (pointOfContact) params.append('pointOfContact', pointOfContact);
      if (status) params.append('status', status);
      if (enrolled !== undefined) params.append('enrolled', enrolled.toString());
      if (developerProgram !== undefined) params.append('developerProgram', developerProgram.toString());
      if (apimPublishedStatus) params.append('apimPublishedStatus', apimPublishedStatus);
      if (lifecycleStage) params.append('lifecycleStage', lifecycleStage);

      const response = await axios.get(
        `${this.apiBaseUrl}/api/admin/reports?${params.toString()}`,
        this.getHeaders(authState)
      );

      return response.data as ReportResponse;
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      throw error;
    }
  }

  /**
   * GET /api/analysis/:apiId
   * Fetch detailed analysis for a single API including all versions
   */
  async getAnalysis(authState: AuthState | null, apiId: string): Promise<ApiAnalysisResponse> {
    try {
      const response = await axios.get(
        `${this.apiBaseUrl}/api/analysis/${apiId}`,
        this.getHeaders(authState)
      );
      return response.data as ApiAnalysisResponse;
    } catch (error) {
      console.error(`Failed to fetch analysis for API ${apiId}:`, error);
      throw error;
    }
  }

  /**
   * POST /api/admin/reports/run
   * Trigger batch reports analysis for all pipeline APIs
   * Requires admin authentication (Bearer token from Azure Function)
   */
  async runAnalysis(authState: AuthState | null, force: boolean = false): Promise<RunAnalysisResponse> {
    try {
      const response = await axios.post(
        `${this.apiBaseUrl}/api/admin/reports/run`,
        { force },
        this.getHeaders(authState)
      );
      return response.data as RunAnalysisResponse;
    } catch (error) {
      console.error('Failed to trigger reports analysis:', error);
      throw error;
    }
  }

  /**
   * POST /api/analysis/:apiId/run
   * Analyze a single API (for testing/manual runs)
   */
  async analyzeSingleApi(authState: AuthState | null, apiId: string, force: boolean = false): Promise<SingleApiAnalysisResponse> {
    try {
      const response = await axios.post(
        `${this.apiBaseUrl}/api/analysis/${apiId}/run?force=${force}`,
        {},
        this.getHeaders(authState)
      );
      return response.data as SingleApiAnalysisResponse;
    } catch (error) {
      console.error('Failed to analyze single API:', error);
      throw error;
    }
  }

  /**
   * POST /api/admin/reports/snapshot
   * Manually trigger snapshot generation (usually called by Azure Function)
   */
  async triggerSnapshot(authState: AuthState | null): Promise<any> {
    try {
      const response = await axios.post(
        `${this.apiBaseUrl}/api/admin/reports/snapshot`,
        {},
        this.getHeaders(authState)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to trigger snapshot:', error);
      throw error;
    }
  }

  /**
   * GET /api/admin/reports/snapshots
   * Fetch historical snapshots with pagination (newest first)
   */
  async getSnapshots(authState: AuthState | null, limit: number = 12, offset: number = 0): Promise<SnapshotsResponse> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await axios.get(
        `${this.apiBaseUrl}/api/admin/reports/snapshots?${params.toString()}`,
        this.getHeaders(authState)
      );
      return response.data as SnapshotsResponse;
    } catch (error) {
      console.error('Failed to fetch snapshots:', error);
      throw error;
    }
  }

  /**
   * GET /api/admin/reports/snapshots/:snapshotId
   * Fetch a specific snapshot by ID
   */
  async getSnapshotById(authState: AuthState | null, snapshotId: string): Promise<ReportSnapshot> {
    try {
      const response = await axios.get(
        `${this.apiBaseUrl}/api/admin/reports/snapshots/${snapshotId}`,
        this.getHeaders(authState)
      );
      return response.data as ReportSnapshot;
    } catch (error) {
      console.error(`Failed to fetch snapshot ${snapshotId}:`, error);
      throw error;
    }
  }

  /**
   * Helper: Export report data as CSV (flattens parent/child structure)
   */
  exportToCsv(data: ReportRow[], filename: string = 'reports.csv', viewMode: 'governance' | 'apim' = 'governance'): void {
    const isGovernance = viewMode === 'governance';

    // Flatten parent/child structure for CSV export
    const flattenedData: any[] = [];
    data.forEach(row => {
      if (row.childVersions && row.childVersions.length > 0) {
        // Export each child version as a separate row
        row.childVersions.forEach(version => {
          flattenedData.push({
            ...row,
            apiId: version.versionId,
            apiVersion: version.versionNumber,
            status: version.status || row.status,
            qualityScore: version.qualityScore !== undefined ? version.qualityScore : row.qualityScore,
            complianceScore: version.complianceScore !== undefined ? version.complianceScore : row.complianceScore,
            findings: version.findings || row.findings,
            lastAnalyzedAt: version.lastAnalyzedAt || row.lastAnalyzedAt,
            isEnrolled: version.isEnrolled !== undefined ? version.isEnrolled : row.isEnrolled,
            // For APIM view
            apimPublishedStatus: version.apimPublishedStatus,
            apimEnvironments: version.apimEnvironments,
            apimLastDiscoveredAt: version.apimLastDiscoveredAt
          });
        });
      } else {
        // Standalone API or legacy format
        flattenedData.push(row);
      }
    });

    const headers = isGovernance ? [
      'API ID',
      'API Name',
      'API Version',
      'Pipeline Enrolled',
      'Line of Business',
      'Product',
      'Area',
      'Point of Contact',
      'Lifecycle Stage',
      'Status',
      'Quality Score (%)',
      'Compliance Score (%)',
      'Findings Count',
      'Last Analyzed',
    ] : [
      'API ID',
      'API Name',
      'Point of Contact',
      'Area',
      'Version & Environments',
      'APIM Products',
      'Published Status',
      'Last Discovered',
    ];

    const rows = flattenedData.map(row => isGovernance ? [
      row.apiId,
      row.fullApiName,
      row.apiVersion || 'N/A',
      row.isEnrolled ? 'Yes' : 'No',
      row.lineOfBusiness || '',
      row.product || '',
      row.area || '',
      row.pointOfContact || '',
      row.lifecycleStage || '',
      row.status || '',
      row.qualityScore?.toString() || '0',
      row.complianceScore?.toString() || '0',
      (row.findings?.length || 0).toString(),
      row.lastAnalyzedAt || '',
    ] : [
      row.apiId,
      row.fullApiName,
      row.pointOfContact || '',
      row.area || '',
      `${row.apiVersion || ''} ${row.apimEnvironments ? `(${row.apimEnvironments})` : ''}`.trim(),
      row.apimProducts || '',
      row.apimPublishedStatus || 'N/A',
      row.apimLastDiscoveredAt || '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  }
}

export default new ReportsService();